/**
 * TOSS Web Application — swipe from the left edge to go back
 *
 * The third way off a full-screen phone screen, beside the app bar's arrow and the device's
 * own back control. In a browser tab the OS already provides this gesture; in an INSTALLED
 * PWA it does not — there is no back swipe and no address bar, so without this the arrow is
 * the only exit, and reaching it one-handed on a tall phone is the reach the gesture exists
 * to avoid.
 *
 * **Why it does not fight the content.** The gesture only arms within {@link EDGE_WIDTH} of
 * the left edge, where no portal screen puts a horizontally scrollable element; anything
 * beginning further right is left alone. It then has to travel {@link TRIGGER_DISTANCE}
 * rightwards while staying inside {@link VERTICAL_SLOP} — a diagonal is someone scrolling,
 * and the moment it looks like one the gesture is abandoned for good rather than re-armed.
 *
 * Listeners are passive: this never calls `preventDefault()`. In a browser tab the OS gesture
 * is what the customer actually gets, and suppressing the platform's own navigation to run
 * ours would be a downgrade. In standalone there is no platform gesture to compete with.
 */

import { useEffect } from 'react';

// ============================================================================
// Constants
// ============================================================================

/** How close to the left edge a touch must start. Matches the platform's own hit zone. */
const EDGE_WIDTH = 24;

/** Rightward travel that commits the gesture. */
const TRIGGER_DISTANCE = 72;

/** Vertical drift tolerated on the way. Beyond it the customer is scrolling. */
const VERTICAL_SLOP = 40;

// ============================================================================
// Hook
// ============================================================================

export interface UseEdgeSwipeBackOptions {
  /** Run the gesture. Pass `false` on every screen that is not a full-screen one. */
  enabled: boolean;
  /** What to do when the gesture completes — normally the same handler as the back arrow. */
  onBack: () => void;
}

export function useEdgeSwipeBack({ enabled, onBack }: UseEdgeSwipeBackOptions): void {
  useEffect(() => {
    if (!enabled) return;

    let startX = 0;
    let startY = 0;
    let tracking = false;

    const onTouchStart = (event: TouchEvent): void => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      if (touch.clientX > EDGE_WIDTH) return;

      startX = touch.clientX;
      startY = touch.clientY;
      tracking = true;
    };

    const onTouchMove = (event: TouchEvent): void => {
      if (!tracking) return;

      const touch = event.touches[0];
      const dx = touch.clientX - startX;
      const dy = Math.abs(touch.clientY - startY);

      // Abandoned, not re-armed: a gesture that drifted into a scroll must not snap back into
      // a navigation if the finger happens to straighten out later in the same drag.
      if (dy > VERTICAL_SLOP || dx < 0) {
        tracking = false;
        return;
      }

      if (dx >= TRIGGER_DISTANCE) {
        tracking = false;
        onBack();
      }
    };

    const stop = (): void => {
      tracking = false;
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', stop, { passive: true });
    document.addEventListener('touchcancel', stop, { passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', stop);
      document.removeEventListener('touchcancel', stop);
    };
  }, [enabled, onBack]);
}

export default useEdgeSwipeBack;
